import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Container, Typography, Paper, Button, Stack, Box, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
  Select, MenuItem, TextField, FormControl, InputLabel, Autocomplete,
  Tabs, Tab, Divider, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { Add, Edit, Delete, Tune, Close, Category, LabelImportant } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL;

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('pt-BR');
};

const parseBRLValue = (str) => {
  if (str === '' || str === null || str === undefined) return '';
  let s = String(str).replace(/R\$\s*/g, '').trim();
  if (!s) return '';
  if (s.includes(',') && s.includes('.')) {
    if (s.lastIndexOf(',') > s.lastIndexOf('.')) {
      s = s.replace(/\./g, '').replace(',', '.');
    } else {
      s = s.replace(/,/g, '');
    }
  } else if (s.includes(',')) {
    s = s.replace(',', '.');
  } else if (s.includes('.')) {
    const parts = s.split('.');
    if (parts.length === 2 && parts[1].length === 3) s = s.replace(/\./g, '');
  }
  const val = parseFloat(s);
  return isNaN(val) ? '' : val;
};

const formatBRLInput = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const n = parseFloat(value);
  if (isNaN(n)) return '';
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
};

const FORM_VAZIO = {
  descricao: '',
  cliente_id: null,
  categoria_id: '',
  tipo_debito_id: null,
  valor: '',
  data_vencimento: '',
  status: 'Pendente',
  forma_recebimento: 'Dinheiro',
  observacao: ''
};

const formasRecebimento = [
  'PIX', 'Dinheiro', 'Cartão de Crédito', 'Cartão de Débito',
  'Transferência Bancária (TED)', 'Transferência Bancária (DOC)',
  'Transferência', 'Cheque', 'Saldo em Conta'
];

const inputStyle = {
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': { borderColor: '#FF8C00' },
    '&.Mui-focused fieldset': { borderColor: '#FF8C00', borderWidth: 2 }
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#FF8C00' }
};

export default function ContasReceber() {
  const [contas, setContas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [tiposDebito, setTiposDebito] = useState([]);

  // Nova Conta / Editar
  const [openDialog, setOpenDialog] = useState(false);
  const [editando, setEditando] = useState(null);
  const [novaConta, setNovaConta] = useState(FORM_VAZIO);
  const [valorDisplay, setValorDisplay] = useState('');

  // Gerenciar Atributos modal
  const [atributosOpen, setAtributosOpen] = useState(false);
  const [atributosTab, setAtributosTab] = useState(0);

  // CRUD Categorias (dentro do modal de atributos)
  const [catOpen, setCatOpen] = useState(false);
  const [catEditando, setCatEditando] = useState(null);
  const [catForm, setCatForm] = useState({ nome: '', descricao: '' });

  // CRUD Tipos de Débito (dentro do modal de atributos)
  const [tipoOpen, setTipoOpen] = useState(false);
  const [tipoEditando, setTipoEditando] = useState(null);
  const [tipoForm, setTipoForm] = useState({ nome: '', descricao: '' });

  // Quick create a partir do botão + nos selects
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickType, setQuickType] = useState(null); // 'categoria' | 'tipo'
  const [quickForm, setQuickForm] = useState({ nome: '', descricao: '' });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const token = () => localStorage.getItem('token');
  const authHeader = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  useEffect(() => {
    carregarContas();
    carregarCategorias();
    carregarClientes();
    carregarTiposDebito();
  }, []);

  // ========== CARREGAMENTOS ==========
  const carregarContas = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/contas-receber`, authHeader());
      setContas(data);
    } catch {
      setSnackbar({ open: true, message: 'Erro ao carregar contas a receber', severity: 'error' });
    }
  };

  const carregarCategorias = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/categorias-pagamento`, authHeader());
      setCategorias(data);
    } catch {
      setCategorias([]);
    }
  };

  const carregarClientes = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/clientes`, authHeader());
      setClientes(data);
    } catch {
      setClientes([]);
    }
  };

  const carregarTiposDebito = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/tipos-debito`, authHeader());
      setTiposDebito(data);
    } catch {
      setTiposDebito([]);
    }
  };

  // ========== NOVA CONTA ==========
  const handleOpenDialog = (conta = null) => {
    if (conta) {
      setNovaConta({
        descricao: conta.descricao || '',
        cliente_id: conta.cliente_id || null,
        categoria_id: conta.categoria_id || '',
        tipo_debito_id: conta.tipo_debito_id || null,
        valor: conta.valor || '',
        data_vencimento: conta.data_vencimento?.split('T')[0] || '',
        status: conta.status || 'Pendente',
        forma_recebimento: conta.forma_recebimento || 'Dinheiro',
        observacao: conta.observacao || ''
      });
      setValorDisplay(formatBRLInput(conta.valor));
      setEditando(conta.id);
    } else {
      setNovaConta(FORM_VAZIO);
      setValorDisplay('');
      setEditando(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditando(null);
    setNovaConta(FORM_VAZIO);
    setValorDisplay('');
  };

  const handleChange = (e) => {
    setNovaConta({ ...novaConta, [e.target.name]: e.target.value });
  };

  const handleValorChange = (e) => {
    setValorDisplay(e.target.value);
    const parsed = parseBRLValue(e.target.value);
    setNovaConta(f => ({ ...f, valor: parsed }));
  };

  const handleValorBlur = () => {
    if (novaConta.valor !== '' && novaConta.valor !== null && novaConta.valor !== undefined) {
      setValorDisplay(formatBRLInput(novaConta.valor));
    }
  };

  const handleSalvar = async () => {
    if (!novaConta.valor || !novaConta.descricao || !novaConta.data_vencimento) {
      setSnackbar({ open: true, message: 'Preencha os campos obrigatórios: descrição, valor e data de vencimento', severity: 'warning' });
      return;
    }
    try {
      const dados = {
        ...novaConta,
        valor: parseFloat(novaConta.valor),
        data_recebimento: novaConta.status === 'Recebido' ? new Date().toISOString().split('T')[0] : null
      };
      if (editando) {
        await axios.put(`${API_URL}/contas-receber/${editando}`, dados, authHeader());
        setSnackbar({ open: true, message: 'Conta atualizada com sucesso!', severity: 'success' });
      } else {
        await axios.post(`${API_URL}/contas-receber`, dados, authHeader());
        setSnackbar({ open: true, message: 'Conta cadastrada com sucesso!', severity: 'success' });
      }
      carregarContas();
      handleCloseDialog();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.mensagem || 'Erro ao salvar conta', severity: 'error' });
    }
  };

  const handleExcluir = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta conta?')) return;
    try {
      await axios.delete(`${API_URL}/contas-receber/${id}`, authHeader());
      setSnackbar({ open: true, message: 'Conta excluída com sucesso!', severity: 'success' });
      carregarContas();
    } catch {
      setSnackbar({ open: true, message: 'Erro ao excluir conta', severity: 'error' });
    }
  };

  // ========== GERENCIAR ATRIBUTOS — CATEGORIAS ==========
  const abrirCatDialog = (cat = null) => {
    if (cat) {
      setCatForm({ nome: cat.nome || '', descricao: cat.descricao || '' });
      setCatEditando(cat.id);
    } else {
      setCatForm({ nome: '', descricao: '' });
      setCatEditando(null);
    }
    setCatOpen(true);
  };

  const fecharCatDialog = () => {
    setCatOpen(false);
    setCatEditando(null);
    setCatForm({ nome: '', descricao: '' });
  };

  const salvarCategoria = async () => {
    if (!catForm.nome.trim()) {
      setSnackbar({ open: true, message: 'Nome é obrigatório', severity: 'warning' });
      return;
    }
    try {
      if (catEditando) {
        await axios.put(`${API_URL}/categorias-pagamento/${catEditando}`, catForm, authHeader());
        setSnackbar({ open: true, message: 'Categoria atualizada!', severity: 'success' });
      } else {
        await axios.post(`${API_URL}/categorias-pagamento`, catForm, authHeader());
        setSnackbar({ open: true, message: 'Categoria criada!', severity: 'success' });
      }
      fecharCatDialog();
      carregarCategorias();
    } catch {
      setSnackbar({ open: true, message: 'Erro ao salvar categoria', severity: 'error' });
    }
  };

  const excluirCategoria = async (id) => {
    if (!window.confirm('Excluir esta categoria?')) return;
    try {
      await axios.delete(`${API_URL}/categorias-pagamento/${id}`, authHeader());
      setSnackbar({ open: true, message: 'Categoria excluída!', severity: 'success' });
      carregarCategorias();
    } catch {
      setSnackbar({ open: true, message: 'Erro ao excluir categoria', severity: 'error' });
    }
  };

  // ========== GERENCIAR ATRIBUTOS — TIPOS DE DÉBITO ==========
  const abrirTipoDialog = (tipo = null) => {
    if (tipo) {
      setTipoForm({ nome: tipo.nome || '', descricao: tipo.descricao || '' });
      setTipoEditando(tipo.id);
    } else {
      setTipoForm({ nome: '', descricao: '' });
      setTipoEditando(null);
    }
    setTipoOpen(true);
  };

  const fecharTipoDialog = () => {
    setTipoOpen(false);
    setTipoEditando(null);
    setTipoForm({ nome: '', descricao: '' });
  };

  const salvarTipoDebito = async () => {
    if (!tipoForm.nome.trim()) {
      setSnackbar({ open: true, message: 'Nome é obrigatório', severity: 'warning' });
      return;
    }
    try {
      if (tipoEditando) {
        await axios.put(`${API_URL}/tipos-debito/${tipoEditando}`, tipoForm, authHeader());
        setSnackbar({ open: true, message: 'Tipo de débito atualizado!', severity: 'success' });
      } else {
        await axios.post(`${API_URL}/tipos-debito`, tipoForm, authHeader());
        setSnackbar({ open: true, message: 'Tipo de débito criado!', severity: 'success' });
      }
      fecharTipoDialog();
      carregarTiposDebito();
    } catch {
      setSnackbar({ open: true, message: 'Erro ao salvar tipo de débito', severity: 'error' });
    }
  };

  const excluirTipoDebito = async (id) => {
    if (!window.confirm('Excluir este tipo de débito?')) return;
    try {
      await axios.delete(`${API_URL}/tipos-debito/${id}`, authHeader());
      setSnackbar({ open: true, message: 'Tipo de débito excluído!', severity: 'success' });
      carregarTiposDebito();
    } catch {
      setSnackbar({ open: true, message: 'Erro ao excluir tipo de débito', severity: 'error' });
    }
  };

  // ========== QUICK CREATE ==========
  const abrirQuickCreate = (type) => {
    setQuickType(type);
    setQuickForm({ nome: '', descricao: '' });
    setQuickOpen(true);
  };

  const salvarQuick = async () => {
    if (!quickForm.nome.trim()) {
      setSnackbar({ open: true, message: 'Nome é obrigatório', severity: 'warning' });
      return;
    }
    try {
      if (quickType === 'categoria') {
        const { data } = await axios.post(`${API_URL}/categorias-pagamento`, quickForm, authHeader());
        await carregarCategorias();
        setNovaConta(f => ({ ...f, categoria_id: data.id || data.insertId || f.categoria_id }));
        setSnackbar({ open: true, message: 'Categoria criada com sucesso!', severity: 'success' });
      } else {
        const { data } = await axios.post(`${API_URL}/tipos-debito`, quickForm, authHeader());
        await carregarTiposDebito();
        setNovaConta(f => ({ ...f, tipo_debito_id: data.id || data.insertId || f.tipo_debito_id }));
        setSnackbar({ open: true, message: 'Tipo de débito criado com sucesso!', severity: 'success' });
      }
      setQuickOpen(false);
    } catch {
      setSnackbar({ open: true, message: 'Erro ao criar item', severity: 'error' });
    }
  };

  // ========== COLUNAS DA TABELA ==========
  const colunas = useMemo(() => [
    { accessorKey: 'id', header: 'ID', size: 60 },
    { accessorKey: 'data_vencimento', header: 'Vencimento', size: 110, Cell: ({ cell }) => formatDate(cell.getValue()) },
    { accessorKey: 'cliente_nome', header: 'Cliente', size: 180, Cell: ({ cell }) => cell.getValue() || '-' },
    { accessorKey: 'descricao', header: 'Descrição', size: 220 },
    { accessorKey: 'tipo_debito_nome', header: 'Tipo de Débito', size: 150, Cell: ({ cell }) => cell.getValue() ? <Chip label={cell.getValue()} size="small" color="warning" variant="outlined" /> : '-' },
    { accessorKey: 'categoria_nome', header: 'Categoria', size: 140, Cell: ({ cell }) => cell.getValue() || '-' },
    { accessorKey: 'valor', header: 'Valor', size: 110, Cell: ({ cell }) => formatCurrency(cell.getValue()) },
    {
      accessorKey: 'status', header: 'Status', size: 110,
      Cell: ({ cell }) => {
        const s = cell.getValue();
        const color = s === 'Recebido' ? 'success' : s === 'Pendente' ? 'warning' : 'error';
        return <Chip label={s} color={color} size="small" />;
      }
    },
    {
      accessorKey: 'acoes', header: 'Ações', size: 100,
      Cell: ({ row }) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Editar">
            <IconButton size="small" color="primary" onClick={() => handleOpenDialog(row.original)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton size="small" color="error" onClick={() => handleExcluir(row.original.id)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    }
  ], []);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* HEADER */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        background: 'linear-gradient(135deg, #1f1f1f 0%, #3a3a3a 100%)',
        p: 3,
        borderRadius: 2,
        color: '#fff'
      }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#f78b00' }}>
            💵 Contas a Receber
          </Typography>
          <Typography variant="body2" sx={{ color: '#aaa', mt: 0.5 }}>
            Gerencie os recebimentos financeiros
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Tune />}
            onClick={() => setAtributosOpen(true)}
            sx={{
              borderColor: '#f78b00',
              color: '#f78b00',
              fontWeight: 600,
              '&:hover': { borderColor: '#f78b00', bgcolor: 'rgba(247,139,0,0.1)' }
            }}
          >
            Gerenciar Atributos
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{
              bgcolor: '#f78b00',
              fontWeight: 700,
              '&:hover': { bgcolor: '#e67e00' }
            }}
          >
            Nova Conta
          </Button>
        </Stack>
      </Box>

      {/* TABELA */}
      <Paper sx={{ p: 2 }}>
        <MaterialReactTable
          columns={colunas}
          data={contas}
          localization={MRT_Localization_PT_BR}
          enablePagination
          enableSorting
          enableColumnFilters
          enableDensityToggle
          initialState={{ density: 'compact', pagination: { pageSize: 25 } }}
          muiTableProps={{ sx: { tableLayout: 'fixed' } }}
        />
      </Paper>

      {/* ========== MODAL: NOVA CONTA / EDITAR ========== */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #333 100%)',
          color: '#f78b00',
          fontWeight: 700,
          fontSize: '1.1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            💵 {editando ? 'Editar Conta a Receber' : 'Nova Conta a Receber'}
          </Box>
          <IconButton onClick={handleCloseDialog} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }} size="small">
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, pt: 2.5 }}>
          <Stack spacing={2.5}>

            {/* Cliente + Valor */}
            <Stack direction="row" spacing={2}>
              <Autocomplete
                sx={{ flex: 1 }}
                options={clientes}
                value={clientes.find(c => c.id === novaConta.cliente_id) || null}
                onChange={(_, val) => setNovaConta(f => ({ ...f, cliente_id: val ? val.id : null }))}
                getOptionLabel={opt => opt?.nome ? `${opt.nome}${opt.cpf_cnpj ? ' — ' + opt.cpf_cnpj : ''}` : ''}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                renderOption={(props, opt) => (
                  <Box component="li" {...props} key={opt.id}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">{opt.nome}</Typography>
                      {opt.cpf_cnpj && <Typography variant="caption" color="text.secondary">{opt.cpf_cnpj}</Typography>}
                    </Box>
                  </Box>
                )}
                renderInput={params => (
                  <TextField {...params} label="Cliente" placeholder="Pesquise por nome ou CPF" sx={inputStyle} />
                )}
              />
              <TextField
                label="Valor (R$) *"
                value={valorDisplay}
                onChange={handleValorChange}
                onBlur={handleValorBlur}
                placeholder="0,00"
                sx={{ width: 160, ...inputStyle }}
                inputProps={{ inputMode: 'decimal' }}
              />
            </Stack>

            <Divider sx={{ borderColor: 'rgba(247,139,0,0.25)' }}>
              <Typography variant="caption" sx={{ color: '#f78b00', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, px: 1 }}>
                Classificação
              </Typography>
            </Divider>

            {/* Tipo de Débito + Categoria */}
            <Stack direction="row" spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                <Autocomplete
                  sx={{ flex: 1 }}
                  options={tiposDebito}
                  value={tiposDebito.find(t => t.id === novaConta.tipo_debito_id) || null}
                  onChange={(_, val) => setNovaConta(f => ({ ...f, tipo_debito_id: val ? val.id : null }))}
                  getOptionLabel={opt => opt?.nome || ''}
                  isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                  noOptionsText="Nenhum tipo cadastrado"
                  renderInput={params => (
                    <TextField {...params} label="Tipo de Débito" sx={inputStyle} />
                  )}
                />
                <Tooltip title="Criar novo tipo de débito">
                  <IconButton
                    onClick={() => abrirQuickCreate('tipo')}
                    sx={{ color: '#f78b00', border: '1px solid rgba(247,139,0,0.5)', borderRadius: 1, '&:hover': { bgcolor: 'rgba(247,139,0,0.08)', borderColor: '#f78b00' } }}
                    size="small"
                  >
                    <Add fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                <Autocomplete
                  sx={{ flex: 1 }}
                  options={categorias}
                  value={categorias.find(c => c.id === novaConta.categoria_id) || null}
                  onChange={(_, val) => setNovaConta(f => ({ ...f, categoria_id: val ? val.id : '' }))}
                  getOptionLabel={opt => opt?.nome || ''}
                  isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                  noOptionsText="Nenhuma categoria cadastrada"
                  renderInput={params => (
                    <TextField {...params} label="Categoria" sx={inputStyle} />
                  )}
                />
                <Tooltip title="Criar nova categoria">
                  <IconButton
                    onClick={() => abrirQuickCreate('categoria')}
                    sx={{ color: '#f78b00', border: '1px solid rgba(247,139,0,0.5)', borderRadius: 1, '&:hover': { bgcolor: 'rgba(247,139,0,0.08)', borderColor: '#f78b00' } }}
                    size="small"
                  >
                    <Add fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            <Divider sx={{ borderColor: 'rgba(247,139,0,0.25)' }}>
              <Typography variant="caption" sx={{ color: '#f78b00', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, px: 1 }}>
                Datas e Pagamento
              </Typography>
            </Divider>

            {/* Data + Forma + Status */}
            <Stack direction="row" spacing={2}>
              <TextField
                name="data_vencimento"
                label="Vencimento *"
                type="date"
                value={novaConta.data_vencimento}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1, ...inputStyle }}
              />
              <FormControl sx={{ flex: 1, ...inputStyle }}>
                <InputLabel>Forma de Recebimento</InputLabel>
                <Select name="forma_recebimento" value={novaConta.forma_recebimento} onChange={handleChange} label="Forma de Recebimento">
                  {formasRecebimento.map(forma => (
                    <MenuItem key={forma} value={forma}>{forma}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ width: 140, ...inputStyle }}>
                <InputLabel>Status</InputLabel>
                <Select name="status" value={novaConta.status} onChange={handleChange} label="Status">
                  <MenuItem value="Pendente">Pendente</MenuItem>
                  <MenuItem value="Recebido">Recebido</MenuItem>
                  <MenuItem value="Cancelado">Cancelado</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {/* Descrição + Observação */}
            <TextField
              name="descricao"
              label="Descrição *"
              value={novaConta.descricao}
              onChange={handleChange}
              fullWidth
              placeholder="Ex: Aluguel de veículo, mensalidade, parcela..."
              sx={inputStyle}
            />

            <TextField
              name="observacao"
              label="Observação"
              value={novaConta.observacao}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
              placeholder="Informações adicionais sobre esta conta..."
              sx={inputStyle}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee', gap: 1 }}>
          <Button onClick={handleCloseDialog} variant="outlined" sx={{ borderColor: '#ddd', color: '#666', '&:hover': { borderColor: '#bbb' } }}>
            Cancelar
          </Button>
          <Button
            onClick={handleSalvar}
            variant="contained"
            sx={{ bgcolor: '#f78b00', fontWeight: 700, px: 4, '&:hover': { bgcolor: '#e07a00' } }}
          >
            {editando ? 'Atualizar' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== MODAL: GERENCIAR ATRIBUTOS ========== */}
      <Dialog
        open={atributosOpen}
        onClose={() => setAtributosOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden', minHeight: 500 } }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #1f1f1f 0%, #3a3a3a 100%)',
          color: '#f78b00',
          fontWeight: 700,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tune sx={{ color: '#f78b00' }} />
            Gerenciar Atributos Financeiros
          </Box>
          <IconButton onClick={() => setAtributosOpen(false)} sx={{ color: '#fff' }} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={atributosTab}
            onChange={(_, v) => setAtributosTab(v)}
            sx={{ px: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 }, '& .Mui-selected': { color: '#f78b00' }, '& .MuiTabs-indicator': { bgcolor: '#f78b00' } }}
          >
            <Tab icon={<Category fontSize="small" />} iconPosition="start" label="Categorias de Pagamento" />
            <Tab icon={<LabelImportant fontSize="small" />} iconPosition="start" label="Tipos de Débito" />
          </Tabs>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {/* ---- ABA: CATEGORIAS ---- */}
          {atributosTab === 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>Categorias de Pagamento</Typography>
                  <Typography variant="caption" color="text.secondary">Endpoint: {API_URL}/categorias-pagamento</Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  size="small"
                  onClick={() => abrirCatDialog()}
                  sx={{ bgcolor: '#f78b00', '&:hover': { bgcolor: '#e67e00' } }}
                >
                  Nova Categoria
                </Button>
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categorias.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ color: '#999', py: 3 }}>
                          Nenhuma categoria cadastrada
                        </TableCell>
                      </TableRow>
                    ) : categorias.map(cat => (
                      <TableRow key={cat.id} hover>
                        <TableCell>{cat.id}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{cat.nome}</TableCell>
                        <TableCell sx={{ color: '#666' }}>{cat.descricao || '-'}</TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Editar">
                              <IconButton size="small" color="primary" onClick={() => abrirCatDialog(cat)}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir">
                              <IconButton size="small" color="error" onClick={() => excluirCategoria(cat.id)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* ---- ABA: TIPOS DE DÉBITO ---- */}
          {atributosTab === 1 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>Tipos de Débito</Typography>
                  <Typography variant="caption" color="text.secondary">Endpoint: {API_URL}/tipos-debito</Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  size="small"
                  onClick={() => abrirTipoDialog()}
                  sx={{ bgcolor: '#f78b00', '&:hover': { bgcolor: '#e67e00' } }}
                >
                  Novo Tipo
                </Button>
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tiposDebito.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ color: '#999', py: 3 }}>
                          Nenhum tipo de débito cadastrado
                        </TableCell>
                      </TableRow>
                    ) : tiposDebito.map(tipo => (
                      <TableRow key={tipo.id} hover>
                        <TableCell>{tipo.id}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{tipo.nome}</TableCell>
                        <TableCell sx={{ color: '#666' }}>{tipo.descricao || '-'}</TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Editar">
                              <IconButton size="small" color="primary" onClick={() => abrirTipoDialog(tipo)}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir">
                              <IconButton size="small" color="error" onClick={() => excluirTipoDebito(tipo.id)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ========== DIALOG: EDITAR/CRIAR CATEGORIA ========== */}
      <Dialog open={catOpen} onClose={fecharCatDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {catEditando ? 'Editar Categoria' : 'Nova Categoria de Pagamento'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1.5 }}>
            <TextField
              label="Nome *"
              value={catForm.nome}
              onChange={e => setCatForm(f => ({ ...f, nome: e.target.value }))}
              fullWidth
              autoFocus
              placeholder="Ex: Aluguel, Multa, Taxa de Serviço..."
              sx={inputStyle}
            />
            <TextField
              label="Descrição"
              value={catForm.descricao}
              onChange={e => setCatForm(f => ({ ...f, descricao: e.target.value }))}
              fullWidth
              multiline
              rows={3}
              placeholder="Descrição detalhada desta categoria..."
              sx={inputStyle}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={fecharCatDialog} variant="outlined" sx={{ borderColor: '#ccc', color: '#666' }}>Cancelar</Button>
          <Button onClick={salvarCategoria} variant="contained" sx={{ bgcolor: '#f78b00', '&:hover': { bgcolor: '#e67e00' } }}>
            {catEditando ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== DIALOG: EDITAR/CRIAR TIPO DE DÉBITO ========== */}
      <Dialog open={tipoOpen} onClose={fecharTipoDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {tipoEditando ? 'Editar Tipo de Débito' : 'Novo Tipo de Débito'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1.5 }}>
            <TextField
              label="Nome *"
              value={tipoForm.nome}
              onChange={e => setTipoForm(f => ({ ...f, nome: e.target.value }))}
              fullWidth
              autoFocus
              placeholder="Ex: Multa por Atraso, Dano no Veículo, Taxa Extra..."
              sx={inputStyle}
            />
            <TextField
              label="Descrição"
              value={tipoForm.descricao}
              onChange={e => setTipoForm(f => ({ ...f, descricao: e.target.value }))}
              fullWidth
              multiline
              rows={3}
              placeholder="Descrição detalhada do tipo de débito..."
              sx={inputStyle}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={fecharTipoDialog} variant="outlined" sx={{ borderColor: '#ccc', color: '#666' }}>Cancelar</Button>
          <Button onClick={salvarTipoDebito} variant="contained" sx={{ bgcolor: '#f78b00', '&:hover': { bgcolor: '#e67e00' } }}>
            {tipoEditando ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== DIALOG: QUICK CREATE ========== */}
      <Dialog open={quickOpen} onClose={() => setQuickOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {quickType === 'categoria' ? 'Nova Categoria' : 'Novo Tipo de Débito'}
          <IconButton onClick={() => setQuickOpen(false)} size="small">
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nome *"
              value={quickForm.nome}
              onChange={e => setQuickForm(f => ({ ...f, nome: e.target.value }))}
              fullWidth
              autoFocus
              placeholder={quickType === 'categoria' ? 'Ex: Aluguel, Taxa...' : 'Ex: Multa, Dano...'}
              sx={inputStyle}
            />
            <TextField
              label="Descrição"
              value={quickForm.descricao}
              onChange={e => setQuickForm(f => ({ ...f, descricao: e.target.value }))}
              fullWidth
              placeholder="Opcional"
              sx={inputStyle}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setQuickOpen(false)} variant="outlined" sx={{ borderColor: '#ccc', color: '#666' }}>Cancelar</Button>
          <Button onClick={salvarQuick} variant="contained" sx={{ bgcolor: '#f78b00', '&:hover': { bgcolor: '#e67e00' } }}>
            Criar e Selecionar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
}
